import React, { useCallback, useRef } from "react";
import { QuestionItem } from "../../../types/form";
import { extractANumberDigits } from "../../../utils/usANumberValidation";

const A_NUMBER_LENGTH = 9;

interface ANumberFieldRendererProps {
  question: QuestionItem;
  inputName: string;
  currentValue: unknown;
  setFormData: React.Dispatch<React.SetStateAction<Record<string, any>>>;
  readOnly?: boolean;
}

/** Left-packed array of exactly 9 single-digit slots from the stored string. */
function digitsArray(value: unknown): string[] {
  const digits = extractANumberDigits(value).slice(0, A_NUMBER_LENGTH);
  const arr = new Array<string>(A_NUMBER_LENGTH).fill("");
  for (let i = 0; i < digits.length; i += 1) {
    arr[i] = digits[i];
  }
  return arr;
}

const ANumberFieldRenderer: React.FC<ANumberFieldRendererProps> = ({
  question,
  inputName,
  currentValue,
  setFormData,
  readOnly = false,
}) => {
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);
  const digits = digitsArray(currentValue);

  const commit = useCallback(
    (arr: string[]) => {
      const next = arr.join("");
      setFormData((prev) => ({
        ...prev,
        [inputName]: next,
      }));
    },
    [inputName, setFormData],
  );

  const setDigitAt = useCallback(
    (idx: number, raw: string) => {
      const digit = raw.replace(/\D/g, "").slice(0, 1);
      const arr = digitsArray(currentValue);
      arr[idx] = digit;
      commit(arr);
      return digit;
    },
    [commit, currentValue],
  );

  const focusAt = (idx: number) => {
    const el = inputRefs.current[idx];
    if (el) {
      el.focus();
      el.select();
    }
  };

  const onChangeAt = (idx: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const digit = setDigitAt(idx, e.target.value);
    if (digit && idx < A_NUMBER_LENGTH - 1) {
      focusAt(idx + 1);
    }
  };

  const onKeyDownAt = (
    idx: number,
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (e.key === "Backspace") {
      const cur = digits[idx];
      if (!cur && idx > 0) {
        e.preventDefault();
        focusAt(idx - 1);
      }
    }
    if (e.key === "ArrowLeft" && idx > 0) {
      e.preventDefault();
      focusAt(idx - 1);
    }
    if (e.key === "ArrowRight" && idx < A_NUMBER_LENGTH - 1) {
      e.preventDefault();
      focusAt(idx + 1);
    }
  };

  const onPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const text = e.clipboardData.getData("text") || "";
    const pasted = extractANumberDigits(text).slice(0, A_NUMBER_LENGTH);
    if (!pasted) return;
    const arr = new Array<string>(A_NUMBER_LENGTH).fill("");
    for (let i = 0; i < pasted.length; i += 1) {
      arr[i] = pasted[i];
    }
    commit(arr);
    const lastIdx = Math.min(pasted.length, A_NUMBER_LENGTH) - 1;
    if (lastIdx >= 0) focusAt(lastIdx);
  };

  return (
    <div className="space-y-2">
      <div
        className="flex flex-wrap items-center gap-2"
        role="group"
        aria-label="A-Number, one digit per box (9 digits)"
      >
        <span className="text-xl font-semibold text-[#1B365D] select-none">
          A-
        </span>
        {digits.map((digit, idx) => (
          <input
            // eslint-disable-next-line react/no-array-index-key
            key={idx}
            ref={(el) => {
              inputRefs.current[idx] = el;
            }}
            type="text"
            inputMode="numeric"
            autoComplete="off"
            name={`${inputName}_${idx}`}
            id={`${inputName}_${idx}`}
            value={digit}
            onChange={(e) => onChangeAt(idx, e)}
            onKeyDown={(e) => onKeyDownAt(idx, e)}
            onPaste={idx === 0 ? onPaste : undefined}
            disabled={readOnly}
            maxLength={1}
            aria-label={`A-Number digit ${idx + 1} of ${A_NUMBER_LENGTH}`}
            aria-describedby={`${inputName}-anumber-hint`}
            className="w-9 h-12 sm:w-10 sm:h-12 text-center text-xl font-semibold border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-100"
          />
        ))}
      </div>
      <p id={`${inputName}-anumber-hint`} className="text-xs text-[#1B365D]/70">
        Enter all nine digits of your A-Number, one digit per box (use leading
        zeros if needed).
      </p>
    </div>
  );
};

export default ANumberFieldRenderer;
